import { createClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

// Configuração do Supabase
// SECURITY: Environment variables are required - no fallback values
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env.local file.'
  )
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: 'painel-barbara-auth',
    storage: localStorage,
  },
})

export type SupabaseClient = typeof supabase

// URLs das Edge Functions
export const EDGE_FUNCTIONS = {
  DASHBOARD: `${supabaseUrl}/functions/v1/dashboard`,
  DASHBOARD_V2: `${supabaseUrl}/functions/v1/dashboard_v2`,
  CLIENTES: `${supabaseUrl}/functions/v1/clientes`,
  GRUPOS: `${supabaseUrl}/functions/v1/grupos`,
  METAS_LOJAS: `${supabaseUrl}/functions/v1/metas_lojas`,
  METAS_VENDEDORES: `${supabaseUrl}/functions/v1/metas_vendedores`,
  DADOS_HISTORICOS: `${supabaseUrl}/functions/v1/dados_historicos`,
} as const

// Helper para headers das Edge Functions
export function getEdgeFunctionHeaders() {
  return {
    Authorization: `Bearer ${supabaseAnonKey}`,
    apikey: supabaseAnonKey,
    'Content-Type': 'application/json',
  }
}

// Exporta a anon key para uso em payloads
export const ANON_KEY = supabaseAnonKey

/** Timeout padrão para chamadas RPC (15 segundos) */
const RPC_TIMEOUT_MS = 15_000

/**
 * Executa um fetch RPC com token e AbortController.
 * Aceita um signal externo (ex: do TanStack Query) para cancelamento na navegação.
 */
async function doRpcFetch(
  functionName: string,
  args: Record<string, unknown>,
  token: string,
  externalSignal?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), RPC_TIMEOUT_MS)

  // Abort when external signal fires (e.g., TanStack Query cancellation on navigation)
  const onExternalAbort = () => controller.abort()
  externalSignal?.addEventListener('abort', onExternalAbort)

  try {
    return await fetch(`${supabaseUrl}/rest/v1/rpc/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseAnonKey,
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
      body: JSON.stringify(args),
      signal: controller.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`RPC ${functionName} timeout após ${RPC_TIMEOUT_MS / 1000}s`)
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
    externalSignal?.removeEventListener('abort', onExternalAbort)
  }
}

/**
 * Chama uma function PostgreSQL via PostgREST RPC diretamente (fetch).
 * Usa o token JWT do usuário logado para autenticação.
 * Inclui timeout via AbortController e auto-retry com refresh em caso de 401.
 * Aceita um signal externo (ex: do TanStack Query) para cancelamento na navegação.
 */
export async function callSupabaseRpc<T>(
  functionName: string,
  args: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T[]> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const token = session?.access_token ?? supabaseAnonKey

  const response = await doRpcFetch(functionName, args, token, signal)

  // 401 (token expirado): refresh da sessão e retry uma vez
  if (response.status === 401 && session) {
    const { data: refreshData } = await supabase.auth.refreshSession()
    const newToken = refreshData.session?.access_token
    if (newToken) {
      const retryResponse = await doRpcFetch(functionName, args, newToken, signal)
      if (!retryResponse.ok) {
        const errorBody = await retryResponse.text()
        throw new Error(`RPC ${functionName} falhou (${retryResponse.status}): ${errorBody}`)
      }
      return retryResponse.json() as Promise<T[]>
    }
  }

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(`RPC ${functionName} falhou (${response.status}): ${errorBody}`)
  }

  return response.json() as Promise<T[]>
}
